import { useCallback, useMemo } from 'react';

import { ICombo, IModifierSelection, IPicker } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import { FIRST_INDEX } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/constants';
import { findCheapestPickerOption } from 'components/product-detail-v2/product-detail-flow/product-picker-flow/utils';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import { selectors, useAppSelector } from 'state/global-state';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { useUIContext } from 'state/ui';
import { isPicker } from 'utils/menu';
import { IWithVendorConfig } from 'utils/vendor-config';

import { computeDiscountPrice, getOfferVendorConfigForPricing } from './utils';

export interface ProductPriceConditions {
  shouldIncludeModifications: boolean;
  shouldIncludeQuantity: boolean;
}

export const useProductPrice = (conditions?: ProductPriceConditions) => {
  const { isOffer, pricingFunction } = useMenuContext();
  const enableCartV2 = useEnableCartV2Experiment();
  const {
    menuObject,
    productQuantity,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();
  const { formatCurrencyForLocale } = useUIContext();

  const { incentiveNotInMenu } = useRedeemReward();
  const { rewardBenefitId, cartIdEditing, isFromCart, currentStep } = useProductWizardFlow();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);

  const selectedOffer = selectedLoyaltyOffer;

  const { shouldIncludeModifications = false, shouldIncludeQuantity = false } = conditions || {};

  const minPriceForPicker = useCallback(() => {
    const pickerData = menuObject as IPicker;
    const firstPickerAspect = pickerData.pickerAspects[FIRST_INDEX];
    const userSelectionsForFirstPickerAspect =
      userSelections.pickerAspects?.[firstPickerAspect._id];

    const quantity = shouldIncludeQuantity ? productQuantity : 1;

    const price = findCheapestPickerOption({
      pickerData,
      pickerAspectOptionIdentifier: userSelectionsForFirstPickerAspect || '',
      pricingFunction,
    });

    return price * quantity;
  }, [
    menuObject,
    pricingFunction,
    productQuantity,
    shouldIncludeQuantity,
    userSelections.pickerAspects,
  ]);

  const totalPrice = useCallback(
    (modifiersInjected?: IModifierSelection[]) => {
      const quantity = shouldIncludeQuantity ? productQuantity : 1;
      const baseTotal = pricingFunction({ item: selectedProduct, quantity });

      let total = baseTotal;
      if (isOffer) {
        const vendorConfigs = getOfferVendorConfigForPricing(selectedOffer);
        const price = vendorConfigs
          ? pricingFunction({
              // TODO: add _type
              item: { vendorConfigs } as IWithVendorConfig,
              quantity,
              modifierSelections: modifiersInjected,
            })
          : 0;
        total = price || selectedOffer?.offerPrice || 0;
      }

      if (shouldIncludeModifications) {
        const comboSlotSelections = transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        );
        const modifierSelections = transformUserSelectionsModifierToModifierSelection(
          userSelections
        );
        const extraTotal =
          pricingFunction({
            item: selectedProduct,
            quantity,
            modifierSelections,
            comboSlotSelections,
          }) - baseTotal;

        total += extraTotal;
      }
      return total;
    },
    [
      isOffer,
      pricingFunction,
      productQuantity,
      selectedOffer,
      selectedProduct,
      shouldIncludeModifications,
      shouldIncludeQuantity,
      userSelections,
    ]
  );

  const discountPrice = useCallback(() => {
    return computeDiscountPrice({
      appliedLoyaltyRewards,
      availableLoyaltyRewardsMap,
      cartIdEditing,
      pricingFunction,
      productQuantity,
      rewardBenefitId,
      selectedProduct,
      menuObject,
      stagedCartPoints,
      enableCartV2,
      isFromCart,
    });
  }, [
    appliedLoyaltyRewards,
    availableLoyaltyRewardsMap,
    cartIdEditing,
    pricingFunction,
    productQuantity,
    rewardBenefitId,
    selectedProduct,
    menuObject,
    stagedCartPoints,
    enableCartV2,
    isFromCart,
  ]);

  const formattedProductPrice = useMemo(() => {
    // On the Product Home page, we want to show the cheapest price for the picker.
    if (isPicker(menuObject) && currentStep === ProductWizardStep.ProductHome) {
      const priceForPicker = minPriceForPicker();
      return formatCurrencyForLocale(priceForPicker);
    }

    const totalWithDiscount = incentiveNotInMenu ? 0 : Math.max(totalPrice() - discountPrice(), 0);
    return formatCurrencyForLocale(totalWithDiscount);
  }, [
    currentStep,
    menuObject,
    incentiveNotInMenu,
    totalPrice,
    discountPrice,
    formatCurrencyForLocale,
    minPriceForPicker,
  ]);

  return {
    totalPrice,
    formattedProductPrice,
  };
};
